import { useMutation } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadBlob } from "@/shared/lib/download";
import { Menu, useToast } from "@/shared/ui";
import { useConversationDetail } from "../hooks/useConversationDetail";
import { type ExportFormat, exportConversation } from "../api/exportConversation";
import { ChevronDownIcon, DownloadIcon, ShareIcon } from "./icons";
import { ShareDialog } from "./ShareDialog";

const TOPBAR_BUTTON =
  "inline-flex h-9 items-center gap-2 rounded-md bg-bubble px-3 font-sans text-[13px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:bg-tint disabled:opacity-50 [&_svg]:size-[15px]";

const EXTENSIONS: Record<ExportFormat, string> = {
  markdown: "md",
  pdf: "pdf",
  json: "json",
  text: "txt",
};

/**
 * Per-conversation topbar actions, shown only on a thread route: a single menu
 * that combines sharing (opens the share dialog) with export. Export hits the
 * backend and downloads the returned file (Markdown / PDF / JSON / plain text).
 */
export function ChatTopbarActions() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? null;
  const [sharing, setSharing] = useState(false);

  // Reads from the same cache the open thread already populated — no extra fetch.
  const detail = useConversationDetail(id);
  const shareToken = detail.data?.conversation.share_token ?? null;

  const exportMutation = useMutation({
    mutationFn: ({ conversationId, format }: { conversationId: string; format: ExportFormat }) =>
      exportConversation(conversationId, format),
    onSuccess: (blob, { format }) => {
      downloadBlob(blob, `conversation.${EXTENSIONS[format]}`);
      toast(t("chat.exported"));
    },
    onError: () => toast(t("chat.exportError")),
  });

  if (!id) return null;

  const run = (format: ExportFormat) => exportMutation.mutate({ conversationId: id, format });

  return (
    <div className="flex items-center gap-2">
      <Menu
        label={t("share.action")}
        align="end"
        side="bottom"
        trigger={({ toggle }) => (
          <button
            type="button"
            data-testid="chat-share-button"
            onClick={toggle}
            disabled={exportMutation.isPending}
            className={TOPBAR_BUTTON}
          >
            <ShareIcon />
            {exportMutation.isPending ? t("chat.exporting") : t("share.action")}
            <ChevronDownIcon className="text-ink-faint" />
          </button>
        )}
        items={[
          {
            id: "share",
            label: t("share.menuShare"),
            icon: <ShareIcon />,
            onSelect: () => setSharing(true),
          },
          {
            id: "markdown",
            label: t("chat.exportMarkdown"),
            icon: <DownloadIcon />,
            separated: true,
            onSelect: () => run("markdown"),
          },
          { id: "pdf", label: t("chat.exportPdf"), icon: <DownloadIcon />, onSelect: () => run("pdf") },
          { id: "json", label: t("chat.exportJson"), icon: <DownloadIcon />, onSelect: () => run("json") },
          {
            id: "text",
            label: t("chat.exportPlainText"),
            icon: <DownloadIcon />,
            onSelect: () => run("text"),
          },
        ]}
      />

      <ShareDialog
        open={sharing}
        conversationId={id}
        existingToken={shareToken}
        onClose={() => setSharing(false)}
      />
    </div>
  );
}
